import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import LightbulbOutlinedIcon from '@mui/icons-material/LightbulbOutlined'
import Spinner from '@/Components/LoadingSpinner'
import { MapIcon, ScrollTop } from '@/Components/UI/Icons'

import MetaTitle from '@/Components/UI/MetaTitle'
import PseudoHeader from '@/Components/PseudoHeader/PseudoHeader'
import InputView from '@/Views/FormSteps/InputView'
import EventSearchInput, { EventSearchMode } from '@/Components/RepeatEvent/EventSearchInput'
import PreviousEventCard, { PreviousEvent } from '@/Components/RepeatEvent/PreviousEventCard'
import ActionBar from '@/Components/UI/ActionBar'
import SearchVenueList from '@/Components/SearchEvent/SearchVenueList'
import VenueCardSkeleton from '@/Components/SearchEvent/VenueCardSkeleton'
import GoogleMap from '@/Components/Recommendations/GoogleMap'
import useBriefContext from '@/Hooks/Brief/useBriefContext'
import useSpaceRecommendationSearch from '@/Hooks/useRecommendation/useSpaceRecommendationSearch'
import fetchLocationByPlaceId from '@/Utilities/Location/fetchLocationByPlaceId'
import { useRecentBookingsQuery, RecentBookingsQuery } from './RecentBookings.generated'
import { isCurrency, CURRENCY_SYMBOLS } from '@/Constants/currencies'
import { DUPLICATE_EVENT_PATH } from '@/Router/Routes/formRoutes'
import { StyleId } from '../../../../Types/generated-types'

const RepeatEventInput: InputView = () => {
  const navigate = useNavigate()
  const { brief } = useBriefContext()
  const [searchMode] = useState<EventSearchMode>('event')
  const [eventSearchValue, setEventSearchValue] = useState('')
  const [venueSearchValue, setVenueSearchValue] = useState('')
  const [locationLabel, setLocationLabel] = useState('')
  const [locationCoords, setLocationCoords] = useState<{ lat: number; lon: number } | null>(null)
  const [locationCity, setLocationCity] = useState<string | null>(null)
  const placesServiceRef = useRef<google.maps.places.PlacesService | null>(null)
  const [hasSearched, setHasSearched] = useState(false)
  const [selectedEventId, setSelectedEventId] = useState<string | null>(null)
  const [selectedVenueIds, setSelectedVenueIds] = useState<number[]>([])
  const [focusedVenueId, setFocusedVenueId] = useState<number | null>(null)

  const getPlacesService = () => {
    if (!placesServiceRef.current && typeof google !== 'undefined') {
      placesServiceRef.current = new google.maps.places.PlacesService(document.createElement('div'))
    }
    return placesServiceRef.current
  }

  const onLocationSelect = async (placeId: string, label: string) => {
    setLocationLabel(label)
    const service = getPlacesService()
    if (!service) return
    const attrs = await fetchLocationByPlaceId({ placeId, placesService: service })
    if (attrs) {
      setLocationCoords({ lat: attrs.lat, lon: attrs.lon })
      setLocationCity(attrs.leadFeedCity ?? null)
    }
  }

  const briefWithLocation = locationCoords
    ? { ...brief, ...locationCoords, ...(locationCity ? { leadFeedCity: locationCity } : {}) }
    : brief

  const {
    spaces: venueResults,
    loading: venueSearchLoading,
    search: searchVenues,
  } = useSpaceRecommendationSearch(briefWithLocation, !!locationCoords && !locationCity)

  const displaySpaces = hasSearched ? venueResults : []
  const displayLoading = hasSearched ? venueSearchLoading : false

  const PAGE_SIZE = 10
  const [pagination, setPagination] = useState<{
    pageSize: number
    lastPaginatedBookingId?: number
  }>({ pageSize: PAGE_SIZE })
  const [allBookings, setAllBookings] = useState<RecentBookingsQuery['recentBookings']>([])
  const [hasMore, setHasMore] = useState(true)
  const sentinelRef = useRef<HTMLDivElement>(null)

  const [{ data, fetching }] = useRecentBookingsQuery({ variables: { pagination } })
  const isLoadingMore = fetching && allBookings.length > 0

  useEffect(() => {
    if (!data?.recentBookings) return
    const incoming = data.recentBookings
    setAllBookings((prev) =>
      pagination.lastPaginatedBookingId ? [...prev, ...incoming] : incoming
    )
    if (incoming.length < PAGE_SIZE) setHasMore(false)
  }, [data])

  const loadMore = useCallback(() => {
    if (fetching || !hasMore) return
    const lastId = allBookings[allBookings.length - 1]?.id
    if (!lastId) return
    setPagination({ pageSize: PAGE_SIZE, lastPaginatedBookingId: lastId })
  }, [fetching, hasMore, allBookings])

  const loadMoreRef = useRef(loadMore)
  loadMoreRef.current = loadMore

  useEffect(() => {
    const el = sentinelRef.current
    if (!el) return
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) loadMoreRef.current()
      },
      { threshold: 0.1 }
    )
    observer.observe(el)
    return () => observer.disconnect()
  }, [])

  const allEvents: PreviousEvent[] = allBookings.map((booking) => ({
    id: String(booking.id),
    bookingName: booking?.name ?? 'Untitled Booking',
    bookedBy:
      [booking.event?.owner?.firstName, booking.event?.owner?.lastName].filter(Boolean).join(' ') ||
      'HeadBox',
    date: booking.fromDateTime ? new Date(booking.fromDateTime).toLocaleDateString('en-GB') : '',
    invoiceAmount:
      booking.budget != null && isCurrency(booking.currency)
        ? `${CURRENCY_SYMBOLS[booking.currency]}${booking.budget.toLocaleString()}`
        : undefined,
    currency: booking.currency ?? undefined,
    location: booking.location ?? undefined,
    lat: booking.lat ?? undefined,
    lon: booking.lon ?? undefined,
    country: booking.country ?? undefined,
    radiusKm: booking.radius ?? undefined,
    locationPolygon: booking.event?.locationPolygon ?? undefined,
    capacity: booking.attendees != null ? String(booking.attendees) : undefined,
    bookedVenue: (() => {
      const space = booking.requestedToBookProposal?.space
      if (!space?.spaceName) return undefined
      const sortedPhotos = space.spacePhotos
        ? [...space.spacePhotos].sort((a, b) => (a.position ?? 0) - (b.position ?? 0))
        : []
      return {
        spaceName: space.spaceName,
        venueName: space.venue?.name ?? space.spaceName,
        imageUrl: sortedPhotos[0]?.url ?? undefined,
        location: space.venue?.city ?? space.venue?.addressLine1 ?? undefined,
        spaceId: space.ID ?? undefined,
        hostId: space.hostContact?.id ?? undefined,
        lat: space.venue?.latitude ?? undefined,
        lng: space.venue?.longitude ?? undefined,
      }
    })(),
    requests: booking.event?.venueRequests
      ?.filter((r) => r.space?.spaceName && r.space?.venue?.name)
      .map((r) => {
        const sortedPhotos = r.space?.spacePhotos
          ? [...r.space.spacePhotos].sort((a, b) => (a.position ?? 0) - (b.position ?? 0))
          : []
        const declinedInvitation = booking.event?.invitations?.find(
          (inv) => inv.recipientType === 'space' && inv.recipientId === r.spaceId && inv.declinedAt
        )
        return {
          spaceName: r.space!.spaceName!,
          venueName: r.space!.venue!.name!,
          imageUrl: sortedPhotos[0]?.url ?? undefined,
          spaceId: r.space!.ID ?? undefined,
          hostId: r.space!.hostContact?.id ?? undefined,
          declinedReason: declinedInvitation?.declinedReason ?? null,
          lat: r.space?.venue?.latitude ?? undefined,
          lng: r.space?.venue?.longitude ?? undefined,
        }
      }),
    layoutId: booking.layoutId ?? undefined,
    cateringNeeds: booking.cateringNeeds ?? undefined,
    additionalNotes: booking.additionalNotes ?? undefined,
    isFlexibleWithDates: booking.isFlexibleWithDates ?? undefined,
    flexibilityNotes: booking.flexibleDatesNote ?? undefined,
    eventTypeId: booking.eventType?.id != null ? String(booking.eventType.id) : undefined,
    styleIds: booking.styles?.map((s) => s.id).filter((id): id is StyleId => id != null) ?? [],
    facilityIds: booking.spaceFacilities?.map((f) => f.id) ?? [],
  }))

  const events = eventSearchValue.trim()
    ? allEvents.filter(
        (e) =>
          e.bookingName.toLowerCase().includes(eventSearchValue.toLowerCase()) ||
          e.date.includes(eventSearchValue)
      )
    : allEvents

  const onToggleEvent = (id: string) => {
    setSelectedEventId((prev) => (prev === id ? null : id))
  }

  const onToggleVenue = (id: number) => {
    setSelectedVenueIds((prev) =>
      prev.includes(id) ? prev.filter((v) => v !== id) : [...prev, id]
    )
  }

  const onSearch = () => {
    if (searchMode === 'venue') {
      const query = venueSearchValue.trim()
      if (!query) {
        setHasSearched(false)
        return
      }
      setHasSearched(true)
      searchVenues(query)
    }
  }

  const searchValue = searchMode === 'event' ? eventSearchValue : venueSearchValue
  const onSearchValueChange = searchMode === 'event' ? setEventSearchValue : setVenueSearchValue

  return (
    <>
      <MetaTitle title='Repeat an event - HeadBox' />
      <PseudoHeader />

      {searchMode === 'venue' ? (
        <div className='relative flex-1 mt-16 pt-6 md:pt-10'>
          <div className='w-full px-2 sm:px-0'>
            <div className='w-full max-w-4xl mx-auto pb-6'>
              <motion.div
                initial={{ opacity: 0, y: -15 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.2, ease: 'easeOut' }}>
                <EventSearchInput
                  value={searchValue}
                  onChange={onSearchValueChange}
                  onSearch={onSearch}
                  searchMode={searchMode}
                  loading={venueSearchLoading}
                  locationValue={locationLabel}
                  onLocationChange={(val) => {
                    setLocationLabel(val)
                    setLocationCoords(null)
                    setLocationCity(null)
                  }}
                  onLocationSelect={onLocationSelect}
                />
              </motion.div>

              <p className='font-normal text-[18px] leading-[28px] text-center py-6'>
                Request as many options as you&apos;d like, we will share them with our <br />{' '}
                experts to start sourcing for you
              </p>
            </div>
          </div>

          <div className='relative w-full px-4 lg:pl-20 lg:pr-8 pb-36 lg:pb-28 flex lg:gap-14'>
            {!displayLoading && !hasSearched && (
              <div
                className='pointer-events-none absolute inset-0 z-10 
              flex flex-col items-center justify-start gap-3 pt-24 px-4 lg:pl-20 lg:pr-8 '>
                <div className='flex flex-row gap-2'>
                  <MapIcon className='w-8 h-8' />
                  <p className='text-2xl font-semibold text-black'>To search for a venue</p>
                </div>

                <p className='text-base sm:text-lg text-[#595959]'>
                  Add a venue name and location to get started
                </p>
              </div>
            )}
            <div className='flex-1 min-w-0'>
              {displayLoading && (
                <div className='flex justify-center pt-8'>
                  <Spinner />
                </div>
              )}
              {!displayLoading && hasSearched && venueResults.length === 0 && (
                <p className='text-base text-[#8c8c8c] text-center pt-8'>
                  No venues found. Try a different search term.
                </p>
              )}
              {!displayLoading && !hasSearched && (
                <div className='flex flex-col gap-4 select-none pointer-events-none blur-xs'>
                  {[0, 1, 2].map((i) => (
                    <VenueCardSkeleton key={i} />
                  ))}
                </div>
              )}
              {!displayLoading && displaySpaces.length > 0 && (
                <SearchVenueList
                  spaces={displaySpaces}
                  selectedIds={selectedVenueIds}
                  onToggle={onToggleVenue}
                  onCardClick={setFocusedVenueId}
                />
              )}
            </div>

            <aside className='hidden lg:block w-5/12 shrink-0'>
              <div className='sticky top-[104px] h-[calc(100vh-200px)] flex flex-col gap-4'>
                <div className='flex-1 min-h-0'>
                  {!hasSearched ? (
                    <>
                      <div className='w-full h-full rounded-xl bg-[#e8e8e8] blu-xs' />
                      <div className='hidden'>
                        <GoogleMap spaces={displaySpaces} />
                      </div>
                    </>
                  ) : (
                    <div className='flex flex-col gap-6 h-full min-h-0'>
                      <GoogleMap
                        spaces={displaySpaces}
                        initialLocation={
                          brief.lat && brief.lon ? { lat: brief.lat, lon: brief.lon } : null
                        }
                        selectedSpaceIds={selectedVenueIds}
                        focusedSpaceId={focusedVenueId}
                      />
                      <div className='flex justify-end items-center gap-2'>
                        <ScrollTop />
                        <button
                          type='button'
                          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                          className='text-base font-medium text-[#1270B8]'>
                          Back to top
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </aside>
          </div>
        </div>
      ) : (
        <div className='relative flex-1 mt-16 pt-6 md:pt-10 px-2 sm:px-0'>
          <div className='w-full max-w-4xl mx-auto pb-36'>
            <div className='flex flex-col gap-6'>
              <motion.div
                initial={{ opacity: 0, y: -15 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.2, ease: 'easeOut' }}>
                <EventSearchInput
                  value={searchValue}
                  onChange={onSearchValueChange}
                  onSearch={onSearch}
                  searchMode={searchMode}
                />
              </motion.div>

              <div className='bg-[rgba(175,35,28,0.05)] rounded-[8px] p-2'>
                <div className='flex items-center gap-2'>
                  <LightbulbOutlinedIcon
                    sx={{ fontSize: 16, color: '#af231c' }}
                    className='shrink-0'
                  />
                  <p className='font-normal text-[18px] !text-[#af231c] leading-[28px]'>
                    Select a previous booking to repeat, you will then be able to edit the details
                    in the next step
                  </p>
                </div>
              </div>

              <p className='font-normal text-[18px] text-black leading-[28px]'>
                Most recent bookings:
              </p>

              <div className='flex flex-col gap-6'>
                {fetching && !isLoadingMore && (
                  <p className='text-[16px] text-[#8c8c8c]'>Loading bookings...</p>
                )}
                {!fetching && events.length === 0 && (
                  <p className='text-[16px] text-[#8c8c8c]'>No recent bookings found.</p>
                )}
                {events.map((event) => (
                  <PreviousEventCard
                    key={event.id}
                    event={event}
                    isSelected={selectedEventId === event.id}
                    onSelect={() => onToggleEvent(event.id)}
                  />
                ))}
                <div ref={sentinelRef} />
                {isLoadingMore && (
                  <div className='flex justify-center py-4'>
                    <Spinner />
                  </div>
                )}
                {!hasMore && allBookings.length > 0 && (
                  <p className='text-[14px] text-[#8c8c8c] text-center py-2'>
                    No more bookings left
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {searchMode === 'venue' ? (
        <ActionBar
          primaryLabel={`Request ${selectedVenueIds.length} venue${
            selectedVenueIds.length !== 1 ? 's' : ''
          }`}
          onPrimary={() => {
            const requests = displaySpaces
              .filter((s) => selectedVenueIds.includes(Number(s.id)))
              .map((s) => ({
                spaceName: s.title,
                venueName: s.venueName,
                imageUrl: s.images[0] ?? undefined,
                location: s.location.city,
                spaceId: Number(s.id),
                hostId: s.hostID,
                lat: s.location.latlng.lat,
                lng: s.location.latlng.lng,
              }))
            navigate(`/${DUPLICATE_EVENT_PATH}`, { state: { event: { requests } } })
          }}
          primaryDisabled={selectedVenueIds.length === 0}
          secondaryLabel='Back'
          onSecondary={() => navigate('/choose-route', { replace: true })}
        />
      ) : (
        <ActionBar
          primaryLabel='Duplicate event'
          onPrimary={() => {
            const selected = allEvents.find((e) => e.id === selectedEventId)
            if (!selected) return
            navigate(`/${DUPLICATE_EVENT_PATH}`, {
              state: {
                event: {
                  id: selected.id,
                  eventName: selected.bookingName,
                  currency: selected.currency,
                  capacity: selected.capacity,
                  requests: [
                    ...(selected.bookedVenue ? [selected.bookedVenue] : []),
                    ...(selected.requests ?? []),
                  ],
                  layoutId: selected.layoutId,
                  cateringNeeds: selected.cateringNeeds,
                  additionalNotes: selected.additionalNotes,
                  isFlexibleWithDates: selected.isFlexibleWithDates,
                  flexibilityNotes: selected.flexibilityNotes,
                  eventTypeId: selected.eventTypeId,
                  styleIds: selected.styleIds,
                  facilityIds: selected.facilityIds,
                  location: selected.location,
                  lat: selected.lat,
                  lon: selected.lon,
                  country: selected.country,
                  radiusKm: selected.radiusKm,
                  locationPolygon: selected.locationPolygon,
                },
              },
            })
          }}
          primaryDisabled={selectedEventId === null}
          secondaryLabel='Back'
          onSecondary={() => navigate('/choose-route', { replace: true })}
        />
      )}
    </>
  )
}

export default RepeatEventInput
